<template>
  <v-data-table
    :footer-props="{
      showFirstLastPage: true,
      firstIcon: 'fas fa-angle-double-left',
      lastIcon: 'fas fa-angle-double-right',
      prevIcon: 'fas fa-angle-left',
      nextIcon: 'fas fa-angle-right',
      'items-per-page-text': 'Resultados por página',
      'items-per-page-options': [10, 25, 50],
    }"
    :headers="headers"
    :item-class="item => getRowClass(item)"
    :items="paginatedRows || tableRows"
    :items-per-page="tablePagination.itemsPerPage"
    :loading="isLoading || tableLoading || isLoadingDealerBikes"
    :loading-text="'Loading...'"
    :no-data-text="$t('common.no-data')"
    :page="tablePagination.page"
    :server-items-length="tableRows.length || 0"
    class="elevation-0 client-history-table"
    @update:items-per-page="onItemsPerPageChange"
    @update:page="onPageChange">
    <template v-slot:item.createDate="{item}">
      <span class="client-history-table__table-cell">{{ item.createDate | formatDate }}</span>
    </template>

    <template v-slot:item.event="{item}">
      <span class="client-history-table__table-cell">{{ $t(`client-history.list.${item.event.toLowerCase()}`) }}</span>
    </template>

    <template v-slot:item.status="{item}">
      <template v-if="item.event === ClientHistoryEventTypes.LOAN">
        <v-icon
          :class="`icon-status ${
            item.status === LoanStatus.Completed
              ? 'green-dot'
              : item.status === LoanStatus.HandedOver
                ? 'yellow-dot'
                : item.status === LoanStatus.Booked
                  ? 'blue-dot'
                  : 'red-dot'
          }`"
          >&#xe025;
        </v-icon>
        <span>
          {{
            item.status === LoanStatus.Completed
              ? $t('dealer-bike.loans.status.completed')
              : item.status === LoanStatus.HandedOver
                ? $t('dealer-bike.loans.status.handed-over')
                : item.status === LoanStatus.Returned
                  ? $t('dealer-bike.loans.status.returned')
                  : item.status === LoanStatus.Started
                    ? $t('dealer-bike.loans.status.started')
                    : item.status === LoanStatus.Cancelled
                      ? $t('dealer-bike.loans.status.cancelled')
                      : $t('dealer-bike.loans.status.booked')
          }}
        </span>
      </template>
      <template v-if="item.event === ClientHistoryEventTypes.OFFER">
        <m-chip
          v-for="({icon, text, variant, tooltip}, index) in [statusChip(item)]"
          :key="index"
          :icon="icon"
          :text="text"
          :tooltip="tooltip"
          :variant="variant" />
      </template>

      <template v-if="item.event === ClientHistoryEventTypes.LEAD">
        <label-tooltip max-width="180px" :is-tooltip-enabled="false">
          <span :class="`text-item status status-${item.status.toLowerCase()}`">{{
            $t(`leads.status.${item.status}`)
          }}</span>
        </label-tooltip>
      </template>
    </template>

    <template v-slot:item.followUp="{item}">
      <template v-if="item.event === ClientHistoryEventTypes.OFFER">
        <v-tooltip v-if="item.expired" :max-width="300" bottom color="black">
          <template v-slot:activator="{on}">
            <p v-on="on" class="text-expired">
              {{ $t('offers.filters.expired') }}
            </p>
          </template>

          <span>{{ $t('offers.tooltip.expired') }}</span>
        </v-tooltip>

        <v-tooltip v-else-if="item.followUp" :max-width="300" bottom color="black">
          <template v-slot:activator="{on}">
            <span :class="`${isPastToday(item.followUp.dueDate) ? 'text-expired' : ''}`" v-on="on">
              {{ item.followUp.dueDate | formatDate }}
            </span>
          </template>

          <span>{{ item.followUp.text }}</span>
        </v-tooltip>
      </template>

      <template v-if="item.event === ClientHistoryEventTypes.LEAD">
        <div v-if="item.followUp">
          <v-tooltip bottom color="black" :max-width="300">
            <template v-slot:activator="{on, attrs}">
              <span :class="`text-item ${isExpiredDate(item.followUp.dueDate)}`" v-bind="attrs" v-on="on">
                {{ item.followUp.dueDate | formatDate }}
              </span>
            </template>
            <span>{{ item.followUp.text }}</span>
          </v-tooltip>
        </div>
      </template>
    </template>

    <template v-slot:item.actions="{item}">
      <offer-table-actions
        v-if="item.event === ClientHistoryEventTypes.OFFER"
        :item="item"
        @changeStatus="changeStatus" />
      <loans-table-actions
        v-if="item.event === ClientHistoryEventTypes.LOAN"
        :item="item"
        :loading="isLoadingDealerBikes"
        :tableList="tableRows"
        @refreshList="$emit('refreshLoansList')" />
      <leads-table-actions
        v-if="item.event === ClientHistoryEventTypes.LEAD"
        :item="item"
        :loading="markLeadLoading"
        :mark-lead-as-read="markAsRead"
        :mask-lead-as-lost="maskAsLost" />
    </template>
  </v-data-table>
</template>
<script>
import {HondaOfferBuilder} from '@muvio/honda-offers-builder'
import {computed, defineComponent, nextTick, ref, watch} from '@vue/composition-api'

import apiOffers from '@/api/offers.api'
import MChip from '@/components/atom/MChip/index.vue'
import {Variant} from '@/components/atom/MChip/types'
import LoansTableActions from '@/components/dealer-bike/LoansTableActions.vue'
import LeadsTableActions from '@/components/leads/LeadsTableActions.vue'
import OfferTableActions from '@/components/offer-manager/OfferTableActions.vue'
import LabelTooltip from '@/components/text/LabelTooltip.vue'
import {useLeadsList} from '@/composables'
import {useGtm} from '@/plugins/gtm'
import {ClientHistoryEventTypes} from '@/types'
import {LoanStatus} from '@/types/dealer-bikes'
import {Status} from '@/types/offers'
import {isPastToday} from '@/utils/date-helper'
import {mapGetters} from '@/utils/map-store'

export default defineComponent({
  name: 'ClientHistoryTable',
  props: ['isLoading', 'tableRows', 'resetPagination'],
  components: {
    MChip,
    OfferTableActions,
    LoansTableActions,
    LabelTooltip,
    LeadsTableActions,
  },
  setup(props, {root, emit}) {
    const {markLeadAsRead, maskLeadAsLost, isLoading: markLeadLoading} = useLeadsList()

    const gtm = useGtm()
    const {getGroup: group, isLoadingDealerBikes} = mapGetters(root.$store.getters)

    watch(
      () => props.resetPagination,
      () => {
        tablePagination.value.page = 1
      },
    )

    const headers = ref([
      {
        text: root.$t('client-history.list.lbl-date'),
        align: 'center',
        value: 'createDate',
        class: 'text-header px-0',
        width: '10%',
        sortable: false,
      },
      {
        text: root.$t('client-history.list.lbl-event'),
        align: 'left',
        value: 'event',
        class: 'text-header',
        width: '15%',
        sortable: false,
      },
      {
        text: root.$t('client-history.list.lbl-model'),
        align: 'left',
        value: 'model',
        class: 'text-header',
        width: '15%',
        sortable: false,
      },
      {
        text: root.$t('client-history.list.lbl-offer-code'),
        align: 'center',
        value: 'offerCode',
        class: 'text-header',
        width: '10%',
        sortable: false,
      },
      {
        text: root.$t('client-history.list.lbl-attended-by'),
        align: 'left',
        value: 'attendedBy',
        class: 'text-header',
        width: '15%',
        sortable: false,
      },
      {
        text: root.$t('client-history.list.lbl-status'),
        align: 'left',
        value: 'status',
        class: 'text-header',
        width: '15%',
        sortable: false,
      },
      {
        text: root.$t('client-history.list.lbl-follow-up'),
        align: 'center',
        value: 'followUp',
        class: 'text-header',
        width: '10%',
        sortable: false,
      },
      {
        text: root.$t('client-history.list.lbl-actions'),
        align: 'right',
        value: 'actions',
        class: 'text-header',
        width: '10%',
        sortable: false,
      },
    ])
    const tableLoading = ref(false)
    const tablePagination = ref({
      page: 1,
      itemsPerPage: 10,
    })

    const pagination = ref({value: {from: 0, size: 10}})

    const paginatedRows = computed(() => {
      const start = (tablePagination.value.page - 1) * tablePagination.value.itemsPerPage
      const end = start + tablePagination.value.itemsPerPage

      return props.tableRows.slice(start, end)
    })

    async function markAsRead(lead, read) {
      await markLeadAsRead(lead, read)
      emit('on-update-lead')
    }

    async function maskAsLost(lead) {
      await maskLeadAsLost(lead)
      emit('on-update-lead')
    }

    function updatePagination({page, itemsPerPage}) {
      if (page) {
        pagination.value.from = calculateFrom({page, itemsPerPage: pagination.value.size})
        tablePagination.value.page = page
      }

      if (itemsPerPage) {
        pagination.value.size = itemsPerPage
        tablePagination.value.itemsPerPage = itemsPerPage
      }
    }

    function onPageChange(page) {
      updatePagination({page})
      document.querySelector('.main-body')?.scrollTo({top: 0})
    }

    function onItemsPerPageChange(itemsPerPage) {
      updatePagination({itemsPerPage})
      tablePagination.value.page = 1
      document.querySelector('.main-body')?.scrollTo({top: 0})
    }

    function calculateFrom({page, itemsPerPage}) {
      return !page ? 0 : (page - 1) * (itemsPerPage || 10)
    }

    function getRowClass(item) {
      let className = 'click client-history-table__row'

      if (item?.read === false) {
        className += '--unread'
      }
      return className
    }

    function hasBeenResponded(offer) {
      return offer.updatedAtStatusAccepted || offer.updatedAtStatusRefused
    }

    function statusChip(offer) {
      const text = root.$t(`offers.status.${offer.status}`)
      const variant = offer.expired ? Variant.Disabled : undefined

      if (offer.status === Status.Pending && hasBeenResponded(offer)) {
        return {
          icon: 'fa-pencil',
          text,
          variant: variant || Variant.Warning,
          tooltip: root.$t(`offers.tooltip.pending-updated`),
        }
      }

      if (offer.status === Status.Pending && !hasBeenResponded(offer)) {
        return {
          icon: 'fa-hourglass',
          text,
          variant: variant || Variant.Warning,
        }
      }

      if (offer.status === Status.Accepted && offer.updatedStatusFromEmail) {
        return {
          icon: 'fa-envelope',
          text,
          variant: variant || Variant.Info,
          tooltip: root.$t('offers.tooltip.accepted-email'),
        }
      }

      if (offer.status === Status.Refused && offer.updatedStatusFromEmail) {
        return {
          icon: 'fa-envelope',
          text,
          variant: Variant.Error,
          tooltip: root.$t('offers.tooltip.rejected-email'),
        }
      }

      if (offer.status === Status.Refused) {
        return {
          icon: 'fa-xmark',
          text,
          variant: Variant.Error,
        }
      }

      if (offer.status === Status.Completed) {
        return {
          text,
          variant: Variant.Success,
        }
      }

      return {
        text,
        variant: variant || Variant.Info,
      }
    }

    async function changeStatus(offer, status) {
      if (!tableLoading.value) {
        gtm?.trackEvent({
          category: 'Offers',
          action: `EditStatusOffer`,
          label: `${offer.id}-${status}`,
        })

        try {
          tableLoading.value = true
          const fullOffer = await apiOffers.getOfferById(offer.id || '', group.value.dealerId)
          const newOffer = new HondaOfferBuilder().fromOffer(fullOffer.data)
          newOffer.status = status

          await apiOffers.editOffer(offer.id || '', newOffer.toOffer(), group.value.dealerId)

          // root.$store.dispatch('loadOffersList', group.value.dealerId)
          tableLoading.value = false

          nextTick(() => {
            offer.status = status
            offer.updatedStatusFromEmail = false
          })
        } catch (err) {
          console.log(err)
          tableLoading.value = false
        }
      }
    }

    function isExpiredDate(date) {
      return isPastToday(date) ? 'expired' : ''
    }

    return {
      headers,
      tableLoading,
      pagination,
      tablePagination,
      LoanStatus,
      ClientHistoryEventTypes,
      paginatedRows,
      onPageChange,
      onItemsPerPageChange,
      getRowClass,
      statusChip,
      isPastToday,
      isExpiredDate,
      changeStatus,
      isLoadingDealerBikes,
      markAsRead,
      maskAsLost,
      markLeadLoading,
    }
  },
})
</script>

<style lang="scss" scoped>
.client-history-table {
  font-family: 'ProximaNovaRegular';
  font-size: 16px;

  &__table-cell {
    display: flex;
    align-items: center;
    height: 80px;
  }

  .icon-status {
    font-family: 'mapitfont-v1' !important;
    font-size: 10px;
    text-transform: none;
    height: 44px;
    font-style: normal;

    &.details-dot {
      height: 32px;
    }

    &.green-dot {
      color: #00803e;
    }
    &.red-dot {
      color: #dc182d;
    }
    &.yellow-dot {
      color: #fca400;
    }
    &.blue-dot {
      color: #3797f0;
    }
  }

  .text-header {
    letter-spacing: 0;
    color: var(--v-gray-lighten5);
    text-align: left;
    font: normal normal 600 13px/15px 'ProximaNovaExCnSBold';
    text-transform: uppercase;
    opacity: 1;
  }

  .text-expired {
    font-size: 14px;
    line-height: 1.4;
    color: #dc182d !important;
    margin: 0;
  }
}

%status {
  content: '';
  width: 10px;
  min-width: 10px;
  height: 10px;
  margin-right: 10px;
  border-radius: 100%;
}

.status {
  display: flex;
  align-items: center;

  &-new:before {
    @extend %status;
    background: #fec478;
  }

  &-in_progress:before {
    @extend %status;
    background: #49bcf4;
  }

  &-with_offer:before {
    @extend %status;
    background: #48a44a;
  }

  &-lost:before {
    @extend %status;
    background: #bdbdbd;
  }
}
</style>

<style lang="scss">
.client-history-table__row {
  &--unread {
    background-color: #ecf3ff;
  }
}
</style>
